
import { supabase } from '@/integrations/supabase/client';
import { Lead } from '@/types/lead';
import { WebSearchData, WebSearchStatus } from '@/types/webSearch';

export interface WebSearchResult {
  summary: string;
  timestamp: string;
  sources: Array<{
    url: string;
    title: string;
    indexRange?: string;
  }>;
  services?: string[];
  keyPeople?: string[];
  companyDetails?: {
    foundedYear?: number;
    [key: string]: any;
  };
  reviews?: {
    rating?: number;
    count?: number;
    [key: string]: any;
  };
  rawText?: string;
  [key: string]: any;
}

/**
 * Trigger web search enrichment for a lead.
 * Returns the enrichment job ID immediately (non-blocking).
 */
export const enrichLeadWithWebSearch = async (lead: Lead, forceReenrich: boolean = false): Promise<string | null> => {
  try {
    console.log(`Requesting web search enrichment for lead: ${lead.id}`);
    
    const { data, error } = await supabase.functions.invoke('enrich-lead', {
      body: { 
        leadId: lead.id,
        leadData: {
          name: lead.name,
          address: lead.address,
          category: lead.category,
          website: lead.website,
          placeId: lead.placeId
        },
        forceReenrich
      }
    });

    if (error) {
      console.error('Error enriching lead with web search:', error);
      return null;
    }

    console.log('Enrichment job created:', data?.jobId);
    return data?.jobId || null;
  } catch (error) {
    console.error('Error in enrichLeadWithWebSearch:', error);
    return null;
  }
};

export const getWebSearchData = async (leadId: string): Promise<WebSearchData | null> => {
  try {
    const { data, error } = await supabase
      .from('leads')
      .select('web_search_data, web_search_updated_at, web_search_status')
      .eq('id', leadId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching web search data:', error);
      return null;
    }

    if (!data || !data.web_search_data) {
      return null;
    }

    const webSearchData = data.web_search_data as any;
    if (webSearchData?.linkedIn?.dataSource === 'verified_linkedin' && data.web_search_status !== 'completed') {
      await supabase
        .from('leads')
        .update({
          web_search_status: 'completed',
          updated_at: new Date().toISOString()
        })
        .eq('id', leadId);
    }

    // Normalize legacy "linkedin" property to "linkedIn"
    const result = data.web_search_data as any;
    if (!result.linkedIn && result.linkedin) {
      result.linkedIn = result.linkedin;
      delete result.linkedin;
    }

    // Sanitize LinkedIn fields to remove placeholders
    const PLACEHOLDERS = [
      'not found', 'not available', 'not specified', 'not mentioned',
      'not explicitly mentioned', 'are not explicitly mentioned',
      'not provided', 'unknown', 'n/a', 'none found', 'no information',
      'information not available', 'information not accessible', 'not accessible',
      'requires login', 'login required', 'requires sign in', 'sign in',
      'sign-in required', 'members only', 'private', 'restricted',
      'access denied', 'publicly unavailable', 'not publicly available',
      'no public information', 'cannot access', 'unable to access', 'content unavailable'
    ];
    
    const isPlaceholder = (v?: string | null) => {
      if (!v) return true;
      const val = v.toLowerCase().trim();
      return !val || PLACEHOLDERS.some(p => val === p || val.includes(p));
    };
    
    const sanitizeVal = (v?: string | null) => isPlaceholder(v) ? null : v!.trim();

    if (result.linkedIn) {
      const li = result.linkedIn;
      li.companyDescription = sanitizeVal(li.companyDescription);
      li.website = sanitizeVal(li.website);
      li.employeeCount = sanitizeVal(li.employeeCount);
      li.industry = sanitizeVal(li.industry);
      li.headquarters = sanitizeVal(li.headquarters);
      if (Array.isArray(li.specialties)) {
        li.specialties = li.specialties.map(sanitizeVal).filter(Boolean);
      }
    }

    return result as WebSearchData;
  } catch (error) {
    console.error('Error in getWebSearchData:', error);
    return null;
  }
};

export const getWebSearchStatus = async (leadId: string): Promise<{
  status: WebSearchStatus;
  updatedAt: Date | null;
  queuedAt: Date | null;
  queuePosition: number | null;
} | null> => {
  try {
    const { data, error } = await supabase
      .from('leads')
      .select('web_search_status, web_search_updated_at, web_search_queue_time, web_search_data')
      .eq('id', leadId)
      .maybeSingle();

    if (error || !data) {
      return null;
    }

    const webSearchData = data.web_search_data as any;
    const liSource = webSearchData?.linkedIn?.dataSource;
    const hasData = webSearchData && (webSearchData.summary || webSearchData.places?.placeId);
    
    let effectiveStatus: WebSearchStatus | null = (data.web_search_status as WebSearchStatus) || null;
    if (liSource === 'verified_linkedin' || (hasData && (effectiveStatus === 'verification_needed' || effectiveStatus === 'not_found'))) {
      effectiveStatus = 'completed';
    }

    let queuePosition = null;
    if (effectiveStatus === 'pending' && data.web_search_queue_time) {
      const { count } = await supabase
        .from('leads')
        .select('*', { count: 'exact', head: true })
        .eq('web_search_status', 'pending')
        .lt('web_search_queue_time', data.web_search_queue_time);

      queuePosition = (count || 0) + 1;
    }

    return {
      status: effectiveStatus,
      updatedAt: data.web_search_updated_at ? new Date(data.web_search_updated_at) : null,
      queuedAt: data.web_search_queue_time ? new Date(data.web_search_queue_time) : null,
      queuePosition,
    };
  } catch (error) {
    console.error('Error in getWebSearchStatus:', error);
    return null;
  }
};

export const getWebSearchUpdateTime = async (leadId: string): Promise<Date | null> => {
  try {
    const { data, error } = await supabase
      .from('leads')
      .select('web_search_updated_at')
      .eq('id', leadId)
      .maybeSingle();

    if (error || !data || !data.web_search_updated_at) {
      return null;
    }

    return new Date(data.web_search_updated_at);
  } catch (error) {
    console.error('Error in getWebSearchUpdateTime:', error);
    return null;
  }
};

export const processWebSearchQueue = async (
  options: { batchSize?: number; prioritizeLeadId?: string } = {}
): Promise<{
  processed: number;
  results: Array<{
    leadId: string;
    status: WebSearchStatus;
    skipped?: boolean;
    error?: string;
  }>;
}> => {
  try {
    const { batchSize = 3, prioritizeLeadId } = options;
    const results: Array<{
      leadId: string;
      status: WebSearchStatus;
      skipped?: boolean;
      error?: string;
    }> = [];

    if (prioritizeLeadId) {
      const { data: targetLead, error: targetError } = await supabase
        .from('leads')
        .select('id, name, address, category, website, web_search_status')
        .eq('id', prioritizeLeadId)
        .maybeSingle();

      if (!targetError && targetLead && targetLead.web_search_status !== 'completed') {
        try {
          const { error: enrichError } = await supabase.functions.invoke('enrich-lead', {
            body: {
              leadId: targetLead.id,
              leadData: {
                name: targetLead.name,
                address: targetLead.address,
                category: targetLead.category,
                website: targetLead.website
              }
            }
          });

          if (enrichError) {
            results.push({ leadId: targetLead.id, status: 'failed', error: enrichError.message });
          } else {
            results.push({ leadId: targetLead.id, status: 'processing' });
          }
        } catch (err) {
          results.push({ leadId: targetLead.id, status: 'failed', error: err instanceof Error ? err.message : 'Unknown error' });
        }
      } else if (targetLead?.web_search_status === 'completed') {
        results.push({ leadId: targetLead.id, status: 'completed', skipped: true });
      }
    }

    let query = supabase
      .from('leads')
      .select('id, name, address, category, website')
      .eq('web_search_status', 'pending')
      .order('web_search_queue_time', { ascending: true })
      .limit(batchSize);
    
    if (prioritizeLeadId && results.length > 0) {
      query = query.neq('id', prioritizeLeadId);
    }

    const { data: pendingLeads, error: fetchError } = await query;
    
    if (fetchError) throw fetchError;
    if (!pendingLeads || pendingLeads.length === 0) return { processed: results.length, results };
    
    for (const lead of pendingLeads) {
      try {
        const { error } = await supabase.functions.invoke('enrich-lead', {
          body: { 
            leadId: lead.id,
            leadData: {
              name: lead.name,
              address: lead.address,
              category: lead.category,
              website: lead.website
            }
          }
        });
        
        if (error) {
          results.push({ leadId: lead.id, status: 'failed', error: error.message });
        } else {
          results.push({ leadId: lead.id, status: 'processing' });
        }
      } catch (error) {
        results.push({ leadId: lead.id, status: 'failed', error: error instanceof Error ? error.message : 'Unknown error' });
      }
    }
    
    return { processed: results.length, results };
  } catch (error) {
    console.error('Error in processWebSearchQueue:', error);
    throw error;
  }
};

export const triggerDeepResearch = async (leadId: string): Promise<boolean> => {
  try {
    console.log(`[DeepResearch] Triggering launcher for lead: ${leadId}`);
    const { data, error } = await supabase.functions.invoke('deep-research', {
      body: { leadId }
    });
    if (error) { console.error('[DeepResearch] Launcher error:', error); return false; }
    console.log('[DeepResearch] Job created:', data?.jobId);
    return true;
  } catch (error) {
    console.error('[DeepResearch] Unexpected error:', error);
    return false;
  }
};

export const getDeepResearchStatus = async (leadId: string): Promise<{ status: string | null; updatedAt: Date | null }> => {
  try {
    const { data, error } = await supabase
      .from('leads')
      .select('deep_research_status, deep_research_updated_at')
      .eq('id', leadId)
      .maybeSingle();
    if (error || !data) return { status: null, updatedAt: null };
    return {
      status: (data as any).deep_research_status || null,
      updatedAt: (data as any).deep_research_updated_at ? new Date((data as any).deep_research_updated_at) : null,
    };
  } catch {
    return { status: null, updatedAt: null };
  }
};

export const verifyLinkedInForLead = async (leadId: string, manualLinkedInUrl: string): Promise<WebSearchData | null> => {
  try {
    const { data, error } = await supabase.functions.invoke('enrich-linkedin', {
      body: { leadId, linkedInUrl: manualLinkedInUrl }
    });
    if (error) throw error;
    return data?.linkedIn || null;
  } catch (error) {
    console.error('Error in verifyLinkedInForLead:', error);
    throw error;
  }
};

export const retryFirecrawlSearch = async (
  leadId: string,
  leadData: { name: string; address?: string; website?: string }
): Promise<string | null> => {
  try {
    // 1. Create enrichment job
    const { data: job, error: jobError } = await supabase
      .from('enrichment_jobs')
      .insert({
        entity_id: leadId,
        entity_type: 'lead',
        job_type: 'web_search',
        status: 'pending',
      } as any)
      .select('id')
      .single();

    if (jobError || !job) {
      console.error('Failed to create enrichment job for firecrawl retry:', jobError);
      return null;
    }

    const jobId = (job as any).id;

    // 2. Invoke enrich-lead with firecrawl_worker stage
    const { error } = await supabase.functions.invoke('enrich-lead', {
      body: {
        stage: 'firecrawl_worker',
        jobId,
        leadId,
        leadData,
      },
    });

    if (error) {
      console.error('Failed to invoke firecrawl retry:', error);
      // Mark the job as failed immediately so it doesn't linger as 'pending'
      await supabase
        .from('enrichment_jobs')
        .update({ status: 'failed', error_message: `Invoke failed: ${error.message || 'unknown'}`, updated_at: new Date().toISOString() } as any)
        .eq('id', jobId);
      return null;
    }

    return jobId;
  } catch (err) {
    console.error('Error in retryFirecrawlSearch:', err);
    return null;
  }
};

export const triggerCrawl = async (leadId: string): Promise<{ ok: boolean; jobId?: string; error?: string }> => {
  try {
    const { data, error } = await supabase.functions.invoke('firecrawl-crawl-lead', {
      body: { leadId },
    });
    if (error) return { ok: false, error: error.message || 'Edge function invocation failed' };
    if (data?.success) return { ok: true, jobId: data.jobId };
    return { ok: false, error: data?.error || 'Unknown error' };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : 'Unexpected error' };
  }
};
